import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Bell, AlertCircle, Clock, User, CheckCircle, XCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';

export default function NotificationEvents() {
  const navigate = useNavigate();
  const { householdId } = useParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'emergency' | 'reminder'

  useEffect(() => {
    fetchEvents();
  }, [householdId, filter]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (householdId) params.append('household_id', householdId);
      if (filter !== 'all') params.append('type', filter);
      params.append('limit', '50');

      const response = await axios.get(`${API}/notification/events?${params.toString()}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Fehler beim Laden der Benachrichtigungen');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'delivered':
        return (
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Zugestellt
          </Badge>
        );
      case 'sent':
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Gesendet
          </Badge>
        );
      case 'partial':
        return (
          <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Teilweise
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            <Loader className="w-3 h-3 mr-1" />
            Ausstehend
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            Fehlgeschlagen
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'emergency':
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <AlertCircle className="w-3 h-3 mr-1" />
            Notfall
          </Badge>
        );
      case 'reminder':
        return (
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
            <Clock className="w-3 h-3 mr-1" />
            Erinnerung
          </Badge>
        );
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lädt Benachrichtigungen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Benachrichtigungs-Verlauf</h1>
          <p className="text-gray-600">Übersicht aller gesendeten Benachrichtigungen</p>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            Alle
          </Button>
          <Button
            variant={filter === 'emergency' ? 'default' : 'outline'}
            onClick={() => setFilter('emergency')}
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            Notfälle
          </Button>
          <Button
            variant={filter === 'reminder' ? 'default' : 'outline'}
            onClick={() => setFilter('reminder')}
          >
            <Clock className="w-4 h-4 mr-2" />
            Erinnerungen
          </Button>
        </div>

        {/* Events List */}
        {events.length === 0 ? (
          <Card>
            <CardContent className="pt-12 pb-12 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold mb-2">Keine Benachrichtigungen</h3>
              <p className="text-gray-600">Es wurden noch keine Benachrichtigungen gesendet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <Card key={event.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getTypeBadge(event.type)}
                        {getStatusBadge(event.status)}
                        <Badge variant="outline">
                          {event.triggered_by}
                        </Badge>
                      </div>
                      
                      <h3 className="text-lg font-semibold mb-1">
                        {event.payload?.message || `${event.type} Benachrichtigung`}
                      </h3>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatTimestamp(event.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {event.recipients.length} Empfänger
                        </span>
                        {/* Phase 8: Delivery & Retry info */}
                        {event.delivered_at && (
                          <span className="flex items-center gap-1 text-emerald-600">
                            <CheckCircle className="w-4 h-4" />
                            Zugestellt: {formatTimestamp(event.delivered_at)}
                          </span>
                        )}
                        {event.retry_count > 0 && (
                          <span className="text-orange-600">
                            🔄 {event.retry_count} Wiederholung(en)
                          </span>
                        )}
                      </div>

                      {/* Payload Details */}
                      {event.payload && Object.keys(event.payload).length > 1 && (
                        <div className="bg-gray-50 rounded-lg p-3 text-sm">
                          <p className="font-semibold mb-1">Details:</p>
                          {event.payload.senior_name && (
                            <p className="text-gray-700">Senior: {event.payload.senior_name}</p>
                          )}
                          {event.payload.trigger_type && (
                            <p className="text-gray-700">Trigger: {event.payload.trigger_type}</p>
                          )}
                          {event.payload.title && (
                            <p className="text-gray-700">Titel: {event.payload.title}</p>
                          )}
                          {event.payload.description && (
                            <p className="text-gray-700">Beschreibung: {event.payload.description}</p>
                          )}
                        </div>
                      )}

                      {/* Phase 7: Provider Results */}
                      {event.provider_results && event.provider_results.length > 0 && (
                        <div className="mt-3 border-t pt-3">
                          <p className="font-semibold text-sm mb-2">Versand-Details (Phase 7):</p>
                          <div className="space-y-1">
                            {event.provider_results.map((result, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs">
                                <Badge 
                                  className={
                                    result.status === 'success' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }
                                >
                                  {result.channel}
                                </Badge>
                                <span className="text-gray-600">
                                  {result.status === 'success' ? '✓' : '✗'} 
                                  {result.message_id ? ` ID: ${result.message_id.substring(0, 8)}...` : ''}
                                  {result.error ? ` ${result.error}` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
